// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps } from "@insite/mobius/GridContainer/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import { TextAreaProps } from "@insite/mobius/TextArea";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { StyledBoxStyles } from "@vanmeter/App/Components/Common/Box";
import { fontConfig } from "@vanmeter/XDL/constants";
import IconEdit from "@vanmeter/XDL/Icons/IconEdit";
import {
    color,
    createLinkIconStyles,
    modalButtonsActionStyles,
    styledScrollbar,
    xScreen,
} from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

export interface CheckoutPageStyles {
    styledBoxStyles?: StyledBoxStyles;
    container?: GridContainerProps;
    sectionGridItem?: GridItemProps;
    sectionGridContainer?: GridContainerProps;
    sectionTitleGridItem?: GridItemProps;
    sectionTitle?: TypographyProps;
    notesTextArea?: TextAreaProps;
    creditBillingAddressSection?: InjectableCss;
    smallHeadingAndLabelStyles?: SmallHeadingAndTextStyles;
}

export const checkoutPageStyles: CheckoutPageStyles = {
    styledBoxStyles: {
        container: {
            css: css`
                background-color: ${color("common.ui2")};
            `,
        },
        contentContainer: {
            css: css`
                padding: 16px;
            `,
        },
        title: {
            variant: "h4",
            forwardAs: "h2",
            css: css`
                padding: 16px 0 12px;
                width: calc(100% - 32px);
                margin: 0 auto;
            `,
        },
    },
    container: {
        gap: 0,
        offsetCss: css`
            gap: 16px 32px;
            align-items: flex-start;
            justify-content: space-between;
            ${xScreen("lte", "md")} {
                gap: 24px;
            }
        `,
    },
    sectionGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            && {
                max-width: calc(50% - 16px);
                ${xScreen("lte", "md")} {
                    max-width: 100%;
                }
            }
        `,
    },
    sectionGridContainer: {
        gap: 0,
        offsetCss: css`
            flex-direction: column;
            gap: 8px;
        `,
    },
    sectionTitleGridItem: {
        width: 12,
        css: css`
            &.title-link {
                gap: 8px 20px;
                align-items: center;
                flex-wrap: wrap;
            }
        `,
    },
    sectionTitle: {
        variant: "h5",
        as: "span",
        css: css`
            margin-bottom: 0;
        `,
    },
    notesTextArea: {
        cssOverrides: {
            formInputWrapper: css`
                display: flex;
            `,
        },
        css: css`
            height: 72px;
        `,
    },
    creditBillingAddressSection: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            width: 100%;
            border-top: 1px solid ${color("common.border")};
            padding-top: 20px;
            margin-top: 4px;
            && {
                max-width: calc(50% - 16px);
                ${xScreen("lte", "md")} {
                    max-width: 100%;
                }
            }
        `,
    },
    smallHeadingAndLabelStyles: {
        wrapper: {
            css: css`
                gap: 8px;

                ${({ width }: any) => {
                    if (width) {
                        return css`
                            width: 100%;
                            max-width: ${width}px;
                        `;
                    }
                }}
            `,
        },
        heading: {
            size: fontConfig.regular.size,
            lineHeight: fontConfig.smallSingleLine.lineHeight,
            fontFamily: fontConfig.secondaryFontFamily,
            weight: fontConfig.weight.bold,
            color: "common.black",
            transform: "capitalize",
        },
        text: {
            ...fontConfig.smallSingleLine,
            color: "common.black",
        },
    },
};

export const sharedSelectorModalStyles = {
    modal: {
        cssOverrides: {
            modalContent: css`
                gap: 0;
                .form-buttons-wrapper {
                    border-top: 1px solid ${color("common.border")};
                    padding-top: 12px;
                }
            `,
        },
    },
    buttonsWrapper: { ...modalButtonsActionStyles.buttonsGridItem },
    cancelButton: {
        variant: "primary",
        buttonType: "outline",
    },
    customerSelectorToolbar: {
        wrapper: {
            css: css`
                padding-bottom: 12px;
                display: flex;
                gap: 12px;
                button,
                nav {
                    margin-left: 0;
                }
                ${TypographyStyle} {
                    white-space: nowrap;
                }
            `,
        },
        searchTextField: {
            cssOverrides: {
                formField: css`
                    flex-basis: 100%;
                `,
            },
        },
        wideCreateNewAddressButton: {
            variant: "primary",
            buttonType: "outline",
        },
    },
    customerSelector: {
        customerCardsWrapper: {
            css: css`
                max-height: 300px;
                overflow-x: hidden;
                ${styledScrollbar()}
            `,
        },
        pagination: {
            cssOverrides: {
                pagination: css`
                    &&& {
                        margin: 0;
                        padding-top: 12px;
                        border-top: 1px solid ${color("common.border")};
                        gap: 8px;
                    }
                `,
            },
        },
        customerCard: {
            container: {
                gap: 0,
                css: css`
                    padding: 8px 8px 8px 0;

                    &:last-child {
                        border-bottom: 0;
                    }
                `,
            },
            actionsWrapper: {
                css: css`
                    align-items: flex-end;
                `,
            },
            selectButton: {
                variant: "link" as any,
                typographyProps: {
                    ...fontConfig.xSmallSingleLine,
                },
            },
            editLink: {
                ...createLinkIconStyles({
                    icon: IconEdit,
                    sizeVariant: "xsmall",
                }),
            },
        },
    },
    editCustomerAddressForm: {
        formFields: {
            container: {
                css: css`
                    padding-right: 8px;
                `,
            },
        },
        submitButton: {
            css: css`
                margin-left: auto;
            `,
        },
    },
    addressFormNote: {
        ...fontConfig.xSmallSingleLine,
        css: css`
            display: block;
            margin-top: 12px;
        `,
    },
} as any;
